/**
 * HybridTrendsAnalytics Component
 *
 * Mockup-aligned implementation with two main views:
 * Part 1: Executive Compliance Scorecard Dashboard
 * Part 2: Location-Specific Deep-Dive Analysis
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { formatPercentage, formatCurrencyDynamic } from '../../../utils/numberUtils';
import '../../dashboard/pages/Dashboards.css';
import './HybridTrendsAnalytics.css';
import '../../../shared/components/common/TrendCard.css';
import TrendsAnalyticsHelpModal from './TrendsAnalyticsHelpModal';

const HybridTrendsAnalytics = ({ token }) => {
  // State management
  const [complianceTrends, setComplianceTrends] = useState(null);
  const [locationTrends, setLocationTrends] = useState(null);
  const [deepDiveLocationTrends, setDeepDiveLocationTrends] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [departmentTrends, setDepartmentTrends] = useState(null);
  const [deepDiveDepartmentTrends, setDeepDiveDepartmentTrends] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [viewMode, setViewMode] = useState('part1'); // 'part1' or 'part2'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [helpSection, setHelpSection] = useState(null);
  const [compareMetric, setCompareMetric] = useState('calibration');
  /** Deep-dive table sort: default worst-first for the selected metric. */
  const [deepDiveDeptSort, setDeepDiveDeptSort] = useState({ key: 'worst', dir: 'asc' });
  const [deepDiveLocSort, setDeepDiveLocSort] = useState({ key: 'worst', dir: 'asc' });
  const [isCompactQuickNav, setIsCompactQuickNav] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const sync = () => setIsCompactQuickNav(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  // Setup axios headers with token
  const getHeaders = () => {
    if (token) {
      return { 'Authorization': `Bearer ${token}` };
    }
    return {};
  };

  /**
   * Period semantics (UI labels → buckets).
   * API still returns weekly `week_N` rows; we map them to the comparison the user expects:
   * - week / month: 2 bars (prior period vs current)
   * - 3 / 6 months: 3 / 6 bars (~one per month, sampled from weekly data)
   * - year: 2 bars (now vs ~52 weeks ago); true multi-year YoY needs calendar-year API (later)
   */
  const PERIOD_CONFIG = {
    week: {
      weeks: 2,
      bars: 2,
      comparisonWeek: 1,
      chartHint: 'Each bar ≈ 1 week · Compare: this week vs last week',
    },
    month: {
      weeks: 4,
      bars: 2,
      comparisonWeek: 3,
      chartHint: '2 calendar months · compliance, complaints & budget · Compare: this month vs last month',
    },
    '3month': {
      weeks: 12,
      bars: 3,
      comparisonWeek: 11,
      chartHint: '3 bars: ~3 months ago → midpoint → current · Compare: now vs 3 months ago',
    },
    '6month': {
      weeks: 24,
      bars: 6,
      comparisonWeek: 23,
      chartHint: '6 bars across ~6 months · Compare: now vs 6 months ago',
    },
    year: {
      weeks: 52,
      bars: 2,
      comparisonWeek: 51,
      chartHint: '2 bars: prior calendar year vs this year YTD · spend & utilization per year',
    },
  };

  const getPeriodConfig = () => PERIOD_CONFIG[selectedPeriod] || PERIOD_CONFIG.week;

  const getPeriodWeeks = () => getPeriodConfig().weeks;

  const DEEP_DIVE_WEEKS = 8;

  const getDeepDiveWeekIndices = () => [7, 6, 5, 4, 3, 2, 1, 0];

  const getEightWeekChange = (trendData, getValue) => {
    if (!trendData) return 0;
    const current = trendData.week_0 ? getValue(trendData.week_0) : 0;
    const prior = trendData.week_7 ? getValue(trendData.week_7) : 0;
    const cur = typeof current === 'number' && !Number.isNaN(current) ? current : 0;
    const prev = typeof prior === 'number' && !Number.isNaN(prior) ? prior : 0;
    return cur - prev;
  };

  /** @deprecated Use getEightWeekChange (always current − 8 weeks ago). */
  const getEightWeekDelta = (trendData, getValue) => getEightWeekChange(trendData, getValue);

  const getPriorPeriodColumnLabel = (trends) => {
    const prior = getComparisonWeek(trends);
    if (prior?.period_label) {
      return formatBudgetPeriodDisplay(prior.period_label);
    }
    const labels = {
      week: 'Prior week',
      month: 'Prior month',
      '3month': '3 months ago',
      '6month': '6 months ago',
      year: 'Prior year YTD',
    };
    return labels[selectedPeriod] || 'Prior period';
  };

  // Fetch compliance trends
  const fetchComplianceTrends = async (weeksParam = null) => {
    try {
      setLoading(true);
      const weeks = weeksParam || getPeriodWeeks();
      const response = await axios.get(API_ENDPOINTS.TRENDS.COMPLIANCE, {
        params: { weeks, period: selectedPeriod },
        headers: getHeaders()
      });
      if (response.data.success) {
        setComplianceTrends(response.data.data);
        setError(null);
      }
    } catch (err) {
      setError('Failed to fetch compliance trends');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch location trends
  const fetchLocationTrends = async (weeksParam = null) => {
    try {
      const weeks = weeksParam || getPeriodWeeks();
      const response = await axios.get(API_ENDPOINTS.TRENDS.LOCATIONS, {
        params: { weeks, period: selectedPeriod },
        headers: getHeaders()
      });
      if (response.data.success) {
        setLocationTrends(response.data.data);
        if (response.data.data.locations.length > 0 && !selectedLocation) {
          setSelectedLocation(response.data.data.locations[0]);
        }
        setError(null);
      }
    } catch (err) {
      setError('Failed to fetch location trends');
      console.error(err);
    }
  };

  // Fetch org-wide departments needing attention (Part 1 — Top 5)
  const fetchDepartmentTrends = async (weeksParam = null) => {
    try {
      const weeks = weeksParam || getPeriodWeeks();
      const response = await axios.get(API_ENDPOINTS.TRENDS.DEPARTMENTS_ATTENTION, {
        params: { weeks, period: selectedPeriod, limit: 5 },
        headers: getHeaders()
      });
      if (response.data.success) {
        setDepartmentTrends(response.data.data);
        setError(null);
      }
    } catch (err) {
      console.error('Failed to fetch department attention trends:', err);
    }
  };

  const fetchDeepDiveLocationTrends = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.TRENDS.LOCATIONS, {
        params: { weeks: DEEP_DIVE_WEEKS },
        headers: getHeaders()
      });
      if (response.data.success) {
        setDeepDiveLocationTrends(response.data.data);
        setError(null);
      }
    } catch (err) {
      setError('Failed to fetch location deep-dive trends');
      console.error(err);
    }
  };

  const fetchDeepDiveDepartmentTrends = async (locationId) => {
    try {
      const response = await axios.get(API_ENDPOINTS.TRENDS.DEPARTMENTS(locationId), {
        params: { weeks: DEEP_DIVE_WEEKS },
        headers: getHeaders()
      });
      if (response.data.success) {
        setDeepDiveDepartmentTrends(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch department deep-dive trends:', err);
    }
  };

  const fetchForecast = async (locationId = null) => {
    try {
      const params = { weeks_ahead: 4 };
      if (locationId) params.location_id = locationId;

      const response = await axios.get(API_ENDPOINTS.TRENDS.FORECAST, {
        params,
        headers: getHeaders()
      });
      if (response.data.success) {
        setForecastData(response.data.data);
        setError(null);
      }
    } catch (err) {
      console.error('Failed to fetch forecast:', err);
    }
  };

  const getSelectedDeepDiveLocation = () => {
    if (!deepDiveLocationTrends?.locations?.length) return null;
    if (selectedLocation) {
      const match = deepDiveLocationTrends.locations.find(
        (loc) => loc.location_id === selectedLocation.location_id
      );
      if (match) return match;
    }
    return deepDiveLocationTrends.locations[0];
  };

  const formatForecastDetail = (metricKey, weeksAhead = 4) => {
    const series = forecastData?.forecast?.[metricKey];
    if (!series?.length) return null;
    const target = series[series.length - 1];
    return `~${Math.round(target)}% in ${weeksAhead} weeks`;
  };

  const DEEP_DIVE_WINDOW_WEEKS = 8;

  /**
   * Project a compliance % forward using the rolling 8-week slope (week_0 vs week_7).
   * `eightWeekChange` is total percentage-point change over the window (same as API velocity).
   */
  const projectFromEightWeekTrend = (current, eightWeekChange, weeksAhead = 4) => {
    const weeklyRate = eightWeekChange / DEEP_DIVE_WINDOW_WEEKS;
    return Math.min(100, Math.max(0, current + weeklyRate * weeksAhead));
  };

  /** Open criticals are a snapshot (stock), not new issues that week. Do not invent a 4-week volume. */
  const formatCriticalIssuesOutlook = (trendData) => {
    if (!trendData?.week_0) return null;
    const current = Math.round(trendData.week_0.critical_issues ?? 0);
    const prior = Math.round(trendData.week_7?.critical_issues ?? 0);
    const net = current - prior;
    if (net === 0) {
      return `${current} open (unchanged over 8 weeks)`;
    }
    const sign = net > 0 ? '+' : '';
    return `${current} open (net ${sign}${net} vs 8 weeks ago)`;
  };

  const buildDeepDiveLocationInsightSummary = (trendData) => {
    if (!trendData?.week_0) {
      return null;
    }
    const metricDefs = [
      { label: 'Calibration', getValue: (w) => w.calibration ?? 0 },
      { label: 'PPM', getValue: (w) => w.ppm ?? 0 },
      { label: 'Regulatory', getValue: (w) => w.regulatory ?? 0 },
      { label: 'Health score', getValue: (w) => w.health_score ?? 0 },
    ];
    const metrics = metricDefs.map((m) => ({
      ...m,
      change: getEightWeekChange(trendData, m.getValue),
      current: m.getValue(trendData.week_0),
    }));
    const improving = metrics.filter((m) => m.change > 0.3);
    const declining = metrics.filter((m) => m.change < -0.3);
    const issuesChange = getEightWeekChange(trendData, (w) => w.critical_issues ?? 0);
    const issuesNow = Math.round(trendData.week_0.critical_issues ?? 0);
    return { metrics, improving, declining, issuesChange, issuesNow };
  };

  const buildDeepDiveDepartmentInsightSummary = (departments, metricKey = 'health_score') => {
    if (!departments?.length) {
      return null;
    }
    const metric = getCompareMetricConfig(metricKey);
    const ranked = departments
      .map((dept) => {
        const current = getBucketMetricValue(dept.trend_data?.week_0, metricKey);
        const prior = getBucketMetricValue(dept.trend_data?.week_7, metricKey);
        const rawGain = current != null && prior != null ? current - prior : null;
        const signedGain =
          rawGain == null ? null : metric.lowerIsBetter ? -rawGain : rawGain;
        return { dept, change: rawGain ?? 0, signedGain, current: current ?? 0 };
      })
      .sort((a, b) => (b.signedGain ?? 0) - (a.signedGain ?? 0));
    const atRisk = ranked.filter((r) =>
      metric.format === 'count' ? r.current >= 3 : r.current < 75
    );
    return { ranked, best: ranked[0], weakest: ranked[ranked.length - 1], atRisk, metric };
  };

  // Utility: Get trend status from velocity
  const getTrendStatus = (velocity) => {
    if (velocity > 0.3) return 'improving';
    if (velocity < -0.3) return 'declining';
    return 'stable';
  };

  // Utility: Text trend arrow (no emoji)
  const getTrendArrow = (velocity) => {
    if (velocity > 0.3) return '↑';
    if (velocity < -0.3) return '↓';
    return '→';
  };

  // Utility: Get comparison text based on selected period
  const getComparisonText = () => {
    switch (selectedPeriod) {
      case 'week':
        return 'vs last week';
      case 'month':
        return 'vs last month';
      case '3month':
        return 'vs 3 months ago';
      case '6month':
        return 'vs 6 months ago';
      case 'year':
        return 'vs last year';
      default:
        return 'vs previous period';
    }
  };

  /** Prior-period bucket for comparison (week_0 = current). */
  const getComparisonWeekIndex = () => getPeriodConfig().comparisonWeek;

  const getComparisonWeek = (trends) => {
    if (!trends) return null;
    return trends[`week_${getComparisonWeekIndex()}`] ?? null;
  };

  /** Raw change current − prior (positive = count/rate went up). */
  const getPeriodMetricChange = (trends, getValue) => {
    const current = trends?.week_0 ? getValue(trends.week_0) : 0;
    const prior = getComparisonWeek(trends) ? getValue(getComparisonWeek(trends)) : 0;
    const cur = typeof current === 'number' && !Number.isNaN(current) ? current : 0;
    const prev = typeof prior === 'number' && !Number.isNaN(prior) ? prior : 0;
    return cur - prev;
  };

  /** Like getPeriodMetricChange, but returns null if either side is missing (e.g. resolution_rate). */
  const getOptionalPeriodMetricChange = (trends, getValue) => {
    if (!trends?.week_0) return null;
    const current = getValue(trends.week_0);
    const priorWeek = getComparisonWeek(trends);
    const prior = priorWeek ? getValue(priorWeek) : null;
    if (typeof current !== 'number' || Number.isNaN(current)) return null;
    if (typeof prior !== 'number' || Number.isNaN(prior)) return null;
    return current - prior;
  };

  /** @deprecated Prefer getPeriodMetricChange for display; kept for callers expecting signed delta. */
  const getPeriodMetricDelta = (trends, getValue, { lowerIsBetter = false } = {}) => {
    const change = getPeriodMetricChange(trends, getValue);
    return lowerIsBetter ? -change : change;
  };

  const getTrendStatusForChange = (change, { lowerIsBetter = false } = {}) =>
    getTrendStatus(lowerIsBetter ? -change : change);

  /** Sparkline keys: left = oldest bucket, right = current (`week_0`). Bar count matches period. */
  const getSparklineWeekKeys = () => {
    const { weeks, bars } = getPeriodConfig();
    if (bars <= 1) {
      return ['week_0'];
    }
    const indices = Array.from({ length: bars }, (_, i) =>
      Math.round((weeks - 1) * (1 - i / (bars - 1)))
    );
    return indices.map((idx) => `week_${idx}`);
  };

  /** Sparkline bar heights (0–100), scaled relative to max in the sampled range. */
  const getSparklineBars = (trends, getValue, weekIndices = null) => {
    if (!trends) {
      return [];
    }
    const keys = weekIndices
      ? weekIndices.map((idx) => `week_${idx}`)
      : getSparklineWeekKeys();
    const values = keys.map((key) => {
      const week = trends[key];
      const raw = week ? getValue(week) : null;
      if (raw == null || (typeof raw === 'number' && Number.isNaN(raw))) {
        return null;
      }
      return typeof raw === 'number' ? raw : null;
    });
    const numeric = values.map((v) => (v == null ? 0 : v));
    const max = Math.max(...numeric, 1);
    const allZero = numeric.every((v) => v === 0);
    return values.map((val, i) => {
      const num = numeric[i];
      let height;
      if (val == null) {
        height = 8;
      } else if (allZero) {
        height = 12;
      } else if (num > 0) {
        height = Math.max(Math.round((num / max) * 100), 20);
      } else {
        height = 8;
      }
      return {
        height,
        isLatest: i === keys.length - 1,
        value: val,
      };
    });
  };

  /** Sum closed/created counts across buckets shown in the sparkline for this period. */
  const getPeriodResolutionTotals = (trends) => {
    if (!trends) {
      return { closed: 0, created: 0 };
    }
    return getSparklineWeekKeys().reduce(
      (acc, key) => {
        const week = trends[key];
        if (week) {
          acc.closed += week.resolution_closed_count || 0;
          acc.created += week.resolution_created_count || 0;
        }
        return acc;
      },
      { closed: 0, created: 0 }
    );
  };

  const defaultSparkBarLabel = (value) => {
    if (value == null || Number.isNaN(value)) {
      return '—';
    }
    return `${Math.round(value)}%`;
  };

  const formatSparkBarSpendLabel = (value) => {
    const v = value ?? 0;
    if (v >= 10000000) {
      return `₹${(v / 10000000).toFixed(1)}Cr`;
    }
    if (v >= 100000) {
      return `₹${(v / 100000).toFixed(1)}L`;
    }
    if (v >= 1000) {
      return `₹${Math.round(v / 1000)}K`;
    }
    return `₹${Math.round(v)}`;
  };

  const renderSparkline = (
    trends,
    getValue,
    { formatBarLabel = defaultSparkBarLabel, weekIndices = null } = {}
  ) => (
    <div className="trend-card-spark">
      {getSparklineBars(trends, getValue, weekIndices).map((bar, i) => (
        <div key={i} className="bar-col">
          <span className={`bar-label ${bar.isLatest ? 'latest' : ''}`} title={formatBarLabel(bar.value)}>
            {formatBarLabel(bar.value)}
          </span>
          <div className="bar-track">
            <div
              className={`bar ${bar.isLatest ? 'latest' : ''}`}
              style={{ height: `${bar.height}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );

  const renderDeepDiveSparkline = (trends, getValue, { formatBarLabel } = {}) =>
    renderSparkline(trends, getValue, {
      formatBarLabel: formatBarLabel || defaultSparkBarLabel,
      weekIndices: getDeepDiveWeekIndices(),
    });

  const renderEightWeekChangeContent = (
    trendData,
    getValue,
    { lowerIsBetter = false, issues = false } = {}
  ) => {
    const change = getEightWeekChange(trendData, getValue);
    const status = getTrendStatusForChange(change, { lowerIsBetter });
    const text = issues
      ? `${change > 0 ? '+' : ''}${Math.round(change)} issues`
      : `${change > 0 ? '+' : ''}${formatPercentage(change)}`;
    return (
      <span className={`metric-change-text metric-change-text--${status}`}>
        {text}
      </span>
    );
  };

  const getEightWeekChangeStatus = (trendData, getValue, { lowerIsBetter = false } = {}) =>
    getTrendStatusForChange(getEightWeekChange(trendData, getValue), { lowerIsBetter });

  /** Badge label from the same improving/declining/stable rules as the status note. */
  const getForecastBadgeLabel = (status, { lowerIsBetter = false } = {}) => {
    if (status === 'improving') return 'Improving';
    if (status === 'declining') return lowerIsBetter ? 'Rising' : 'Declining';
    return 'Stable';
  };

  /**
   * 4-week outlook text: prefer forecast API series; else linear projection from the
   * rolling 8-week slope (same basis as the status note).
   */
  const formatMetricOutlook = (
    trendData,
    getValue,
    { metricKey = null, issues = false } = {}
  ) => {
    if (issues) {
      return formatCriticalIssuesOutlook(trendData) || '8-week trend projection';
    }
    if (metricKey) {
      const apiDetail = formatForecastDetail(metricKey);
      if (apiDetail) return apiDetail;
    }
    if (!trendData?.week_0) return 'Insufficient data';
    const current = getValue(trendData.week_0) ?? 0;
    const change = getEightWeekChange(trendData, getValue);
    const projected = projectFromEightWeekTrend(current, change);
    return `~${Math.round(projected)}% in 4 weeks`;
  };

  const renderForecastCell = (
    trendData,
    getValue,
    { metricKey = null, lowerIsBetter = false, issues = false } = {}
  ) => {
    const status = getEightWeekChangeStatus(trendData, getValue, { lowerIsBetter });
    return (
      <td className="forecast-cell">
        <div className={`forecast-badge forecast-badge--${status}`}>
          {getForecastBadgeLabel(status, { lowerIsBetter })}
        </div>
        <div className="forecast-detail">
          {formatMetricOutlook(trendData, getValue, { metricKey, issues })}
        </div>
      </td>
    );
  };

  /** Table cell: period sparkline + bold current value + delta vs prior bucket. */
  const renderMetricTrendCell = (
    trendData,
    getValue,
    { lowerIsBetter = false, formatValue = (v) => formatPercentage(v), formatDelta } = {}
  ) => {
    if (!trendData?.week_0) {
      return <span className="metric-trend-empty">—</span>;
    }
    const current = getValue(trendData.week_0);
    const curNum = typeof current === 'number' && !Number.isNaN(current) ? current : 0;
    const change = getPeriodMetricChange(trendData, getValue);
    const fmtDelta =
      formatDelta ||
      ((d) => (lowerIsBetter ? String(Math.round(Math.abs(d))) : formatPercentage(Math.abs(d))));
    const trendStatus = getTrendStatusForChange(change, { lowerIsBetter });
    const deltaClass =
      trendStatus === 'improving' ? 'positive' : trendStatus === 'declining' ? 'negative' : 'neutral';
    const sign = change > 0 ? '+' : change < 0 ? '−' : '';

    return (
      <div className="metric-trend-cell">
        {renderSparkline(trendData, getValue, {
          formatBarLabel: lowerIsBetter
            ? (v) => String(Math.round(v ?? 0))
            : (v) => `${Math.round(v ?? 0)}%`,
        })}
        <div className="metric-trend-current">{formatValue(curNum)}</div>
        <div className={`metric-trend-delta ${deltaClass}`}>
          {change !== 0 && <span>{sign}</span>}
          {fmtDelta(change)}
          <span className="metric-trend-delta-period"> {getComparisonText()}</span>
        </div>
      </div>
    );
  };

  const COMPARE_METRICS = [
    { key: 'calibration', label: 'Calibration', format: 'pct', lowerIsBetter: false },
    { key: 'ppm', label: 'PPM', format: 'pct', lowerIsBetter: false },
    { key: 'regulatory', label: 'Regulatory', format: 'pct', lowerIsBetter: false },
    { key: 'health_score', label: 'Health Score', format: 'pct', lowerIsBetter: false },
    { key: 'critical_issues', label: 'Open Critical Issues', format: 'count', lowerIsBetter: true },
  ];

  const getCompareMetricConfig = (key = compareMetric) =>
    COMPARE_METRICS.find((m) => m.key === key) || COMPARE_METRICS[0];

  const getCompareMetricCurrentHeader = (metric = getCompareMetricConfig()) =>
    `Current (${metric.label})`;

  const toggleDeepDiveSort = (table, key) => {
    const setter = table === 'dept' ? setDeepDiveDeptSort : setDeepDiveLocSort;
    setter((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      // First click on a column: sensible default (name A→Z, numbers worst→best via asc on signedGain/current helpers)
      return { key, dir: key === 'name' ? 'asc' : 'asc' };
    });
  };

  const sortCompareRows = (rows, sort, metric, { nameKey }) => {
    const dir = sort.dir === 'desc' ? -1 : 1;
    const sorted = [...rows];
    sorted.sort((a, b) => {
      if (sort.key === 'name') {
        const an = nameKey(a).localeCompare(nameKey(b), undefined, { sensitivity: 'base' });
        return an * dir;
      }
      if (sort.key === 'current') {
        const av = a.current;
        const bv = b.current;
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        // Worst first when dir=asc: pct → lower first; issues → higher first
        const cmp = metric.lowerIsBetter ? bv - av : av - bv;
        return cmp * dir;
      }
      if (sort.key === 'prior') {
        const av = a.prior;
        const bv = b.prior;
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        const cmp = metric.lowerIsBetter ? bv - av : av - bv;
        return cmp * dir;
      }
      if (sort.key === 'delta') {
        const av = a.rawGain;
        const bv = b.rawGain;
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        // Worst first when dir=asc: most negative signed movement first
        const as = a.signedGain ?? 0;
        const bs = b.signedGain ?? 0;
        return (as - bs) * dir;
      }
      // 'worst' (default): signed gain worst first, then current level
      const as = a.signedGain ?? 0;
      const bs = b.signedGain ?? 0;
      if (as !== bs) return (as - bs) * dir;
      const av = a.current;
      const bv = b.current;
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const level = metric.lowerIsBetter ? bv - av : av - bv;
      return level * dir;
    });
    return sorted;
  };

  const renderSortableTh = (table, sort, key, label, { className = '' } = {}) => {
    const active =
      sort.key === key || (key === 'delta' && sort.key === 'worst');
    const arrow = active ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '';
    return (
      <th className={`sortable-th ${className}`.trim()}>
        <button
          type="button"
          className={`sortable-th-btn${active ? ' is-active' : ''}`}
          onClick={() => toggleDeepDiveSort(table, key)}
          title="Click to sort"
        >
          {label}
          {arrow}
        </button>
      </th>
    );
  };

  const setCompareMetricAndResetSort = (metricKey) => {
    setCompareMetric(metricKey);
    setDeepDiveDeptSort({ key: 'worst', dir: 'asc' });
    setDeepDiveLocSort({ key: 'worst', dir: 'asc' });
  };

  /** Read a metric from a location or department week bucket (null-safe). */
  const getBucketMetricValue = (week, metricKey) => {
    if (!week) return null;
    if (metricKey === 'health_score') {
      const v = week.health_score ?? week.compliance;
      return v != null && !Number.isNaN(v) ? Number(v) : null;
    }
    const v = week[metricKey];
    return v != null && !Number.isNaN(v) ? Number(v) : null;
  };

  const formatCompareMetricValue = (value, format) => {
    if (value == null || Number.isNaN(value)) return 'N/A';
    if (format === 'count') return String(Math.round(value));
    return formatPercentage(value);
  };

  const formatCompareMetricDelta = (rawGain, format) => {
    if (rawGain == null || Number.isNaN(rawGain)) return 'N/A';
    if (format === 'count') {
      const rounded = Math.round(rawGain);
      return `${rounded > 0 ? '+' : ''}${rounded}`;
    }
    return `${rawGain > 0 ? '+' : ''}${formatPercentage(rawGain)}`;
  };

  const renderCompareMetricSelect = (idSuffix = '') => (
    <label className="metric-explorer-select" htmlFor={`compare-metric-${idSuffix || 'main'}`}>
      <span className="metric-explorer-select-label">Metric</span>
      <select
        id={`compare-metric-${idSuffix || 'main'}`}
        value={compareMetric}
        onChange={(e) => setCompareMetricAndResetSort(e.target.value)}
        aria-label="Metric to compare"
      >
        {COMPARE_METRICS.map((m) => (
          <option key={m.key} value={m.key}>{m.label}</option>
        ))}
      </select>
    </label>
  );

  /** Default explorer metric: largest 8-week decline (or critical rise) for this location. */
  const pickDefaultCompareMetric = (trendData) => {
    if (!trendData?.week_0) return 'calibration';
    let worstKey = 'calibration';
    let worstSigned = Infinity;
    COMPARE_METRICS.forEach((m) => {
      const current = trendData.week_0?.[m.key];
      const prior = trendData.week_7?.[m.key];
      if (current == null || prior == null) return;
      const raw = current - prior;
      const signed = m.lowerIsBetter ? -raw : raw; // lower = worse for ranking default
      if (signed < worstSigned) {
        worstSigned = signed;
        worstKey = m.key;
      }
    });
    return worstKey;
  };

  const openLocationDeepDive = (loc) => {
    if (!loc) return;
    setSelectedLocation({
      location_id: loc.location_id,
      location_name: loc.location_name,
    });
    setViewMode('part2');
  };

  const getTrajectoryForGain = (gain, { maxGain, minGain } = {}) => {
    if (gain > 0.3) {
      if (maxGain != null && gain >= maxGain - 0.01 && maxGain > 0.3) {
        return { label: 'Fastest', status: 'improving' };
      }
      return { label: 'Improving', status: 'improving' };
    }
    if (gain < -0.3) {
      if (minGain != null && gain <= minGain + 0.01 && minGain < -0.3) {
        return { label: 'Slowest', status: 'declining' };
      }
      return { label: 'Declining', status: 'declining' };
    }
    return { label: 'Stable', status: 'stable' };
  };

  const getDepartmentRiskLevel = (trendData) => {
    const w0 = trendData?.week_0 || {};
    const scores = [w0.calibration, w0.ppm, w0.regulatory, w0.health_score ?? w0.compliance]
      .filter((v) => v != null)
      .map(Number);
    if (scores.length === 0) {
      return { label: 'Good', tone: 'improving' };
    }
    const minCategory = Math.min(...scores);
    if (minCategory < 60) {
      return { label: 'Critical', tone: 'declining' };
    }
    if (minCategory < 75) {
      return { label: 'Warning', tone: 'stable' };
    }
    return { label: 'Good', tone: 'improving' };
  };

  const getOverallLocationStatus = (trendData) => getDepartmentRiskLevel(trendData);

  const getDepartmentTrendAssessment = (trendData) => {
    const categoryLabels = { calibration: 'Cal', ppm: 'PPM', regulatory: 'Reg' };
    const byStatus = { improving: [], declining: [], stable: [] };
    const compareWeek = getComparisonWeek(trendData);

    Object.entries(categoryLabels).forEach(([key, label]) => {
      const current = trendData?.week_0?.[key];
      const prior = compareWeek?.[key];
      if (current == null || prior == null) return;
      const status = getTrendStatus(current - prior);
      byStatus[status].push(label);
    });

    const improving = byStatus.improving.length;
    const declining = byStatus.declining.length;
    const stable = byStatus.stable.length;
    const scored = improving + declining + stable;
    if (scored === 0) {
      return { text: 'Stable', status: 'stable', detailParts: null };
    }
    const allSame =
      improving === scored || declining === scored || stable === scored;

    const detailParts = [];
    if (byStatus.improving.length) {
      detailParts.push({ status: 'improving', text: `Improving: ${byStatus.improving.join(', ')}` });
    }
    if (byStatus.stable.length) {
      detailParts.push({ status: 'stable', text: `Stable: ${byStatus.stable.join(', ')}` });
    }
    if (byStatus.declining.length) {
      detailParts.push({ status: 'declining', text: `Declining: ${byStatus.declining.join(', ')}` });
    }
    const detailPartsOrNull = allSame ? null : detailParts;

    // Overall badge stays in the same three labels as help + status chips.
    if (declining >= 2 || declining > improving) {
      return { text: 'Declining', status: 'declining', detailParts: detailPartsOrNull };
    }
    if (improving >= 2 || improving > declining) {
      return { text: 'Improving', status: 'improving', detailParts: detailPartsOrNull };
    }
    return { text: 'Stable', status: 'stable', detailParts: detailPartsOrNull };
  };

  const renderStatusBadge = ({ label, tone }) => (
    <span className={`forecast-badge forecast-badge--${tone}`}>{label}</span>
  );

  const formatResolutionRate = (week) => {
    if (!week || week.resolution_rate == null) {
      return 'N/A';
    }
    return formatPercentage(week.resolution_rate);
  };

  /** Turn API period_label (ISO range or month/year) into a short readable label. */
  const formatBudgetPeriodDisplay = (periodLabel) => {
    if (!periodLabel) {
      return 'Selected period';
    }
    const rangeMatch = periodLabel.match(/^(\d{4}-\d{2}-\d{2})\s*[–-]\s*(\d{4}-\d{2}-\d{2})$/);
    if (!rangeMatch) {
      return periodLabel;
    }
    const start = new Date(`${rangeMatch[1]}T12:00:00`);
    const end = new Date(`${rangeMatch[2]}T12:00:00`);
    const opts = { month: 'short', year: 'numeric' };
    const sameMonth =
      start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
    if (sameMonth) {
      const monthYear = end.toLocaleDateString('en-IN', opts);
      return `${start.getDate()}–${end.getDate()} ${monthYear}`;
    }
    const startStr = start.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    const endStr = end.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${startStr} – ${endStr}`;
  };

  const renderBudgetScorecard = () => {
    if (!complianceTrends?.week_0) {
      return null;
    }
    const cur = complianceTrends.week_0;
    const prev = getComparisonWeek(complianceTrends);
    const isYearView = selectedPeriod === 'year';
    // Utilization is calendar YTD — only YoY compare is a meaningful trend.
    const utilDelta = isYearView
      ? getOptionalPeriodMetricChange(complianceTrends, (w) =>
          w.budget_utilization != null ? w.budget_utilization : null
        )
      : null;
    const periodDisplay = formatBudgetPeriodDisplay(cur.period_label);
    const budgetRef = formatCurrencyDynamic(cur.annual_budget ?? 0);
    const utilTrendClass =
      utilDelta == null ? 'stable' : getTrendStatus(utilDelta);

    return (
      <div
        className={`trend-card trend-card--budget ${utilTrendClass}`}
        title={`YTD utilization = calendar-year spend ÷ ${budgetRef} approved today. Org budget is today’s total (historical budgets are not stored); prior-year % uses the same reference. Within-year Compare views show YTD level only (not a period trend).`}
      >
        <div className="trend-card-label">Budget Utilization (YTD)</div>
        <div className="trend-card-value">
          {cur.budget_utilization != null ? formatPercentage(cur.budget_utilization) : 'N/A'}
        </div>
        <div className="budget-meta">
          <div><strong>{periodDisplay}</strong> — {formatCurrencyDynamic(cur.period_spend ?? 0)} spent</div>
          <div>YTD: {formatCurrencyDynamic(cur.ytd_spend ?? 0)} of {budgetRef}</div>
          {isYearView && prev && (
            <div>{formatBudgetPeriodDisplay(prev.period_label)}: {prev.budget_utilization != null ? formatPercentage(prev.budget_utilization) : 'N/A'} ({formatCurrencyDynamic(prev.ytd_spend ?? 0)})</div>
          )}
        </div>
        <div className="trend-card-delta">
          {utilDelta != null ? (
            <>
              <span className="arrow">{getTrendArrow(utilDelta)}</span>
              <span>{utilDelta > 0 ? '+' : ''}{formatPercentage(utilDelta)}</span>
              <span className="period">{getComparisonText()} (same YTD dates)</span>
            </>
          ) : (
            <span className="period">YTD level · period trend on Year compare</span>
          )}
        </div>
        {renderSparkline(complianceTrends, (w) => w.period_spend ?? 0, {
          formatBarLabel: formatSparkBarSpendLabel,
        })}
      </div>
    );
  };

  const renderSectionHeading = (title, helpKey) => (
    <div className="trends-section-heading">
      <h2>{title}</h2>
      {helpKey && (
        <button
          type="button"
          className="user-mgmt-help-btn"
          onClick={() => setHelpSection(helpKey)}
          aria-label={`${title} help`}
          title="What do these metrics mean?"
        >
          ?
        </button>
      )}
    </div>
  );

  const renderWindowNote = (content, { insight = false } = {}) => (
    <div
      className={`list-default-window-note${insight ? ' list-default-window-note--insight' : ''}`}
      role={insight ? 'status' : 'note'}
    >
      <span className="list-default-window-note__icon" aria-hidden="true">
        {insight ? '⚠️' : 'ℹ️'}
      </span>
      <span className="list-default-window-note__body">{content}</span>
    </div>
  );

  const renderDeepDiveLocationStatusNote = (location) => {
    const summary = buildDeepDiveLocationInsightSummary(location.trend_data);
    if (!summary) {
      return null;
    }
    const { improving, declining, issuesChange, issuesNow, metrics } = summary;
    const hasDeclines = declining.length > 0;
    const headline =
      improving.length >= 3
        ? 'Strong 8-week improvement'
        : declining.length >= 2
          ? 'Mixed 8-week performance'
          : '8-week performance';
    const issuesOutlook = formatCriticalIssuesOutlook(location.trend_data);
    const forecastBits = forecastData
      ? [
          `Calibration: ${formatForecastDetail('calibration') || '—'}`,
          `PPM: ${formatForecastDetail('ppm') || '—'}`,
          `Regulatory: ${formatForecastDetail('regulatory') || '—'}`,
          `Health: ${formatForecastDetail('health_score') || '—'}`,
          `Issues: ${issuesOutlook || '—'}`,
        ].join('. ')
      : `Forecast API unavailable. Issues outlook: ${issuesOutlook || 'insufficient 8-week data'}`;
    const action = hasDeclines
      ? `Prioritize ${declining.map((m) => m.label.toLowerCase()).join(' and ')} — largest 8-week declines in this window.`
      : 'Sustain current compliance momentum and monitor critical-issue volume.';

    return renderWindowNote(
      <>
        <strong>{headline}.</strong>
        {improving.length > 0 && (
          <>
            {' '}
            Improving:{' '}
            {improving.map((m) => `${m.label} (+${formatPercentage(m.change)})`).join(', ')}.
          </>
        )}
        {declining.length > 0 && (
          <>
            {' '}
            Declining:{' '}
            {declining.map((m) => `${m.label} (${formatPercentage(m.change)})`).join(', ')}.
          </>
        )}
        {metrics.every((m) => Math.abs(m.change) <= 0.3) && (
          <> Compliance metrics are largely stable vs 8 weeks ago.</>
        )}
        {' '}
        Critical issues: {issuesNow} active ({issuesChange > 0 ? '+' : ''}
        {Math.round(issuesChange)} over 8 weeks).
        {' '}
        <strong>4-week outlook:</strong> {forecastBits}. {action}
      </>,
      { insight: hasDeclines }
    );
  };

  const renderFinancialTrends = () => {
    if (!complianceTrends?.week_0) {
      return null;
    }
    const cur = complianceTrends.week_0;
    const capexDelta = getPeriodMetricDelta(complianceTrends, (w) => w.period_capex ?? 0);
    const opexDelta = getPeriodMetricDelta(complianceTrends, (w) => w.period_opex ?? 0);
    const isYearView = selectedPeriod === 'year';
    const utilDelta = isYearView
      ? getOptionalPeriodMetricChange(complianceTrends, (w) =>
          w.budget_utilization != null ? w.budget_utilization : null
        )
      : null;
    const utilTrendClass = utilDelta == null ? 'stable' : getTrendStatus(utilDelta);

    return (
      <>
        {renderWindowNote(
          <>
            Capex and maintenance opex are spend in the selected Compare window.
            Utilization is calendar-year YTD (trend delta only on Year compare). Use{' '}
            <strong>?</strong> for definitions.
          </>
        )}
        <div className="trend-card-grid">
          <div className={`trend-card ${getTrendStatus(capexDelta)}`}>
            <div className="trend-card-label">Period CAPEX</div>
            <div className="trend-card-value">{formatCurrencyDynamic(cur.period_capex ?? 0)}</div>
            <div className="trend-card-delta">
              <span className="arrow">{getTrendArrow(capexDelta)}</span>
              <span>{capexDelta > 0 ? '+' : ''}{formatCurrencyDynamic(capexDelta)}</span>
              <span className="period">{getComparisonText()}</span>
            </div>
          </div>

          <div className={`trend-card ${utilTrendClass}`}>
            <div className="trend-card-label">Budget Utilization (YTD)</div>
            <div className="trend-card-value">
              {cur.budget_utilization != null ? formatPercentage(cur.budget_utilization) : 'N/A'}
            </div>
            <div className="trend-card-delta">
              {utilDelta != null ? (
                <>
                  <span className="arrow">{getTrendArrow(utilDelta)}</span>
                  <span>{utilDelta > 0 ? '+' : ''}{formatPercentage(utilDelta)}</span>
                  <span className="period">{getComparisonText()} (same YTD dates)</span>
                </>
              ) : (
                <span className="period">YTD level · period trend on Year compare</span>
              )}
            </div>
          </div>

          <div className={`trend-card ${getTrendStatus(-opexDelta)}`}>
            <div className="trend-card-label">Period OPEX (Maintenance)</div>
            <div className="trend-card-value">{formatCurrencyDynamic(cur.period_opex ?? 0)}</div>
            <div className="trend-card-delta">
              <span className="arrow">{getTrendArrow(-opexDelta)}</span>
              <span>{opexDelta > 0 ? '+' : ''}{formatCurrencyDynamic(opexDelta)}</span>
              <span className="period">{getComparisonText()}</span>
            </div>
          </div>
        </div>
      </>
    );
  };

  // Consolidated refetch function for manual refresh
  const refetchAllData = useCallback(() => {
    if (token) {
      setError(null);
      if (viewMode === 'part1') {
        fetchComplianceTrends();
        fetchLocationTrends();
        fetchDepartmentTrends();
      } else if (viewMode === 'part2' && selectedLocation) {
        fetchDeepDiveLocationTrends();
        fetchDeepDiveDepartmentTrends(selectedLocation.location_id);
        fetchForecast(selectedLocation.location_id);
      }
    }
  }, [token, viewMode, selectedLocation]);

  // Initial load
  useEffect(() => {
    if (token) {
      fetchComplianceTrends();
      fetchLocationTrends();
      fetchDepartmentTrends();
    }
  }, [token]);

  // Refetch Part 1 data when Compare period changes
  useEffect(() => {
    if (token && viewMode === 'part1') {
      fetchComplianceTrends();
      fetchLocationTrends();
      fetchDepartmentTrends();
    }
  }, [selectedPeriod]);

  // Part 2: fixed 8-week rolling fetch (independent of Compare)
  useEffect(() => {
    if (token && viewMode === 'part2') {
      fetchDeepDiveLocationTrends();
    }
  }, [token, viewMode]);

  useEffect(() => {
    if (token && viewMode === 'part2' && selectedLocation?.location_id) {
      fetchDeepDiveDepartmentTrends(selectedLocation.location_id);
      fetchForecast(selectedLocation.location_id);
    }
  }, [token, viewMode, selectedLocation?.location_id]);

  // When entering deep-dive or switching location, default explorer to worst-moving metric.
  useEffect(() => {
    if (viewMode !== 'part2' || !deepDiveLocationTrends?.locations?.length) return;
    const loc =
      deepDiveLocationTrends.locations.find((l) => l.location_id === selectedLocation?.location_id) ||
      deepDiveLocationTrends.locations[0];
    if (loc?.trend_data) {
      setCompareMetricAndResetSort(pickDefaultCompareMetric(loc.trend_data));
    }
  }, [viewMode, selectedLocation?.location_id, deepDiveLocationTrends]);

  if (loading) {
    return <div className="trends-loading">Loading trends data...</div>;
  }

  // PART 1: EXECUTIVE SCORECARD DASHBOARD
  if (viewMode === 'part1') {
    return (
      <div className="hybrid-trends-container">
        {/* Header Section */}
        <div className="trends-header">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <h1 className="page-title">📈 Performance Trends</h1>
            <button
              onClick={refetchAllData}
              className="btn-refresh"
              title="Refresh trends data"
              aria-label="Refresh trends data"
              style={{ padding: '0.5rem', fontSize: '1.2rem', border: 'none', background: 'none', cursor: 'pointer', lineHeight: 1, flexShrink: 0, marginTop: '0.25rem' }}
            >
              🔄
            </button>
          </div>
          <p className="page-subtitle" style={{ margin: 0 }}>
            Compare periods to see what is improving, declining, or stable.
          </p>
        </div>

        {error && <div className="trends-error">{error}</div>}

        {/* View Mode Toggle - Positioned at Top */}
        <div className="view-mode-toggle">
          <button className="view-mode-btn" onClick={() => setViewMode('part2')}>
            Location deep-dive →
          </button>
        </div>

        {/* Quick Navigation Bar */}
        <div
          className={`quick-nav-bar admin-quick-nav${isCompactQuickNav ? ' admin-quick-nav--compact' : ''}`}
          style={{
            padding: isCompactQuickNav ? '0.65rem 0 0.85rem' : undefined,
            /* Below sticky .app-header (z-index 100, slim shell); top:0 hid the bar under the header on narrow viewports */
            top: 'var(--app-header-offset)',
            zIndex: 55
          }}
        >
          <div className="quick-nav-label">Jump to</div>
          <div className="admin-quick-nav-scroll">
            <div
              className="quick-nav-links admin-quick-nav-links"
              style={{
                display: 'flex',
                flexWrap: isCompactQuickNav ? 'nowrap' : 'wrap',
                gap: '0.5rem',
                alignItems: 'center'
              }}
            >
              {[
                { label: '🎯 Scorecard', id: 'section-compliance' },
                { label: '🏆 Locations', id: 'section-locationPerformance' },
                { label: '🏥 Departments', id: 'section-departmentTrends' },
                { label: '💰 Financial', id: 'section-financial' }
              ].map((link) => (
                <a
                  key={link.id}
                  href={`#${link.id}`}
                  className="quick-nav-link admin-quick-nav-link"
                  onClick={(e) => {
                    e.preventDefault();
                    const element = document.getElementById(link.id);
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Period Selector */}
        <div className="period-selector">
          <span className="period-selector-label">Compare:</span>
          <button className={`period-btn ${selectedPeriod === 'week' ? 'active' : ''}`} onClick={() => {
            setSelectedPeriod('week');
            setLoading(true);
          }}>
            This Week vs Last Week
          </button>
          <button className={`period-btn ${selectedPeriod === 'month' ? 'active' : ''}`} onClick={() => {
            setSelectedPeriod('month');
            setLoading(true);
          }}>
            This Month vs Last Month
          </button>
          <button className={`period-btn ${selectedPeriod === '3month' ? 'active' : ''}`} onClick={() => {
            setSelectedPeriod('3month');
            setLoading(true);
          }}>
            Last 3 Months
          </button>
          <button className={`period-btn ${selectedPeriod === '6month' ? 'active' : ''}`} onClick={() => {
            setSelectedPeriod('6month');
            setLoading(true);
          }}>
            Last 6 Months
          </button>
          <button className={`period-btn ${selectedPeriod === 'year' ? 'active' : ''}`} onClick={() => {
            setSelectedPeriod('year');
            setLoading(true);
          }}>
            Year-over-Year
          </button>
        </div>
        {renderWindowNote(
          <>
            <strong>{getPeriodConfig().chartHint}.</strong> Card values are the latest bucket;
            deltas show change {getComparisonText()}.
          </>
        )}

        {/* Compliance Scorecard Section */}
        <section className="trends-section" id="section-compliance">
          {renderSectionHeading('🎯 Performance Scorecard', 'scorecard')}
          {renderWindowNote(
            <>
              Latest Compare bucket for each metric. Deltas vs start of range.
              Sparklines run oldest → newest. Use <strong>?</strong> for how each metric is calculated.
            </>
          )}

          {complianceTrends && complianceTrends.week_0 && (
            <div className="trend-card-grid">
              {/* Calibration Compliance */}
              <div className={`trend-card ${getTrendStatus(getPeriodMetricDelta(complianceTrends, (w) => w.calibration || 0))}`}>
                <div className="trend-card-label">Calibration Compliance</div>
                <div className="trend-card-value">{formatPercentage(complianceTrends.week_0.calibration)}</div>
                <div className="trend-card-delta">
                  <span className="arrow">{getTrendArrow(getPeriodMetricDelta(complianceTrends, (w) => w.calibration || 0))}</span>
                  <span>{getPeriodMetricDelta(complianceTrends, (w) => w.calibration || 0) > 0 ? '+' : ''}{formatPercentage(getPeriodMetricDelta(complianceTrends, (w) => w.calibration || 0))}</span>
                  <span className="period">{getComparisonText()}</span>
                </div>
                {renderSparkline(complianceTrends, (w) => w.calibration || 0)}
              </div>

              {/* PPM Compliance */}
              <div className={`trend-card ${getTrendStatus(getPeriodMetricDelta(complianceTrends, (w) => w.ppm || 0))}`}>
                <div className="trend-card-label">PPM Compliance</div>
                <div className="trend-card-value">{formatPercentage(complianceTrends.week_0.ppm)}</div>
                <div className="trend-card-delta">
                  <span className="arrow">{getTrendArrow(getPeriodMetricDelta(complianceTrends, (w) => w.ppm || 0))}</span>
                  <span>{getPeriodMetricDelta(complianceTrends, (w) => w.ppm || 0) > 0 ? '+' : ''}{formatPercentage(getPeriodMetricDelta(complianceTrends, (w) => w.ppm || 0))}</span>
                  <span className="period">{getComparisonText()}</span>
                </div>
                {renderSparkline(complianceTrends, (w) => w.ppm || 0)}
              </div>

              {/* Regulatory Compliance */}
              <div className={`trend-card ${getTrendStatus(getPeriodMetricDelta(complianceTrends, (w) => w.regulatory || 0))}`}>
                <div className="trend-card-label">Regulatory Compliance</div>
                <div className="trend-card-value">{formatPercentage(complianceTrends.week_0.regulatory)}</div>
                <div className="trend-card-delta">
                  <span className="arrow">{getTrendArrow(getPeriodMetricDelta(complianceTrends, (w) => w.regulatory || 0))}</span>
                  <span>{getPeriodMetricDelta(complianceTrends, (w) => w.regulatory || 0) > 0 ? '+' : ''}{formatPercentage(getPeriodMetricDelta(complianceTrends, (w) => w.regulatory || 0))}</span>
                  <span className="period">{getComparisonText()}</span>
                </div>
                {renderSparkline(complianceTrends, (w) => w.regulatory || 0)}
              </div>

              {/* Overall Device Health */}
              <div className={`trend-card ${getTrendStatus(getPeriodMetricDelta(complianceTrends, (w) => w.health_score || 0))}`}>
                <div className="trend-card-label">Overall Device Health</div>
                <div className="trend-card-value">{formatPercentage(complianceTrends.week_0.health_score)}</div>
                <div className="trend-card-delta">
                  <span className="arrow">{getTrendArrow(getPeriodMetricDelta(complianceTrends, (w) => w.health_score || 0))}</span>
                  <span>{getPeriodMetricDelta(complianceTrends, (w) => w.health_score || 0) > 0 ? '+' : ''}{formatPercentage(getPeriodMetricDelta(complianceTrends, (w) => w.health_score || 0))}</span>
                  <span className="period">{getComparisonText()}</span>
                </div>
                {renderSparkline(complianceTrends, (w) => w.health_score || 0)}
              </div>

              {/* Critical Issues Count */}
              <div
                className={`trend-card ${getTrendStatusForChange(getPeriodMetricChange(complianceTrends, (w) => w.critical_issues || 0), { lowerIsBetter: true })}`}
                title="Active critical complaints (open, acknowledged, or in progress)"
              >
                <div className="trend-card-label">Critical Issues</div>
                <div className="trend-card-value">{Math.round(complianceTrends.week_0.critical_issues || 0)}</div>
                <div className="trend-card-delta">
                  <span className="arrow">{getTrendArrow(-getPeriodMetricChange(complianceTrends, (w) => w.critical_issues || 0))}</span>
                  <span>
                    {(() => {
                      const c = getPeriodMetricChange(complianceTrends, (w) => w.critical_issues || 0);
                      return `${c > 0 ? '+' : ''}${Math.round(c)} issues`;
                    })()}
                  </span>
                  <span className="period">{getComparisonText()}</span>
                </div>
                {renderSparkline(complianceTrends, (w) => w.critical_issues || 0, {
                  formatBarLabel: (v) => String(Math.round(v ?? 0)),
                })}
              </div>

              {/* Complaint Resolution Rate */}
              {(() => {
                const resolutionDelta = getOptionalPeriodMetricChange(
                  complianceTrends,
                  (w) => (w.resolution_rate != null ? w.resolution_rate : null)
                );
                const resolutionStatus =
                  resolutionDelta == null ? 'stable' : getTrendStatus(resolutionDelta);
                const { closed, created } = getPeriodResolutionTotals(complianceTrends);
                return (
              <div
                className={`trend-card ${resolutionStatus}`}
                title={`${closed} closed by period end / ${created} created in selected period.`}
              >
                <div className="trend-card-label">Resolution Rate</div>
                <div className="trend-card-value">{formatResolutionRate(complianceTrends.week_0)}</div>
                <div className="trend-card-delta">
                  {resolutionDelta != null ? (
                    <>
                      <span className="arrow">{getTrendArrow(resolutionDelta)}</span>
                      <span>
                        {resolutionDelta > 0 ? '+' : ''}
                        {formatPercentage(resolutionDelta)}
                      </span>
                      <span className="period">{getComparisonText()}</span>
                    </>
                  ) : (
                    <span className="period">No comparable rate {getComparisonText()}</span>
                  )}
                </div>
                {renderSparkline(complianceTrends, (w) => w.resolution_rate, {
                  formatBarLabel: (v) =>
                    v == null || Number.isNaN(v) ? 'N/A' : `${Math.round(v)}%`,
                })}
              </div>
                );
              })()}

              {renderBudgetScorecard()}
            </div>
          )}
        </section>

        {/* Location Performance Trends Table */}
        <section className="trends-section" id="section-locationPerformance">
          {renderSectionHeading('🏆 Location Performance', 'location')}
          {renderWindowNote(
            <>
              Locations ranked by attention for the selected Compare period (risk, then decline).
              Click a row to open the location deep-dive. Use <strong>?</strong> for status rules.
            </>
          )}

          {locationTrends && locationTrends.locations.length > 0 && (
            <div className="comparison-table-scroll">
                <table className="comparison-table comparison-table--metric-trends">
                  <thead>
                    <tr>
                      <th>Location</th>
                      <th>Calibration</th>
                      <th>PPM</th>
                      <th>Regulatory</th>
                      <th>Health Score</th>
                      <th>Critical Issues</th>
                      <th>Risk Level</th>
                    </tr>
                  </thead>
                  <tbody>
                    {locationTrends.locations.map((loc) => {
                      const status = getOverallLocationStatus(loc.trend_data);
                      return (
                        <tr
                          key={loc.location_id}
                          className="location-row-clickable"
                          onClick={() => openLocationDeepDive(loc)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openLocationDeepDive(loc);
                            }
                          }}
                          tabIndex={0}
                          role="link"
                          aria-label={`Open deep-dive for ${loc.location_name}`}
                        >
                          <td className="metric-table-name">
                            📍 {loc.location_name}
                            <span className="location-row-hint">Deep-dive →</span>
                          </td>
                          <td>{renderMetricTrendCell(loc.trend_data, (w) => w.calibration ?? 0)}</td>
                          <td>{renderMetricTrendCell(loc.trend_data, (w) => w.ppm ?? 0)}</td>
                          <td>{renderMetricTrendCell(loc.trend_data, (w) => w.regulatory ?? 0)}</td>
                          <td>{renderMetricTrendCell(loc.trend_data, (w) => w.health_score ?? 0)}</td>
                          <td>
                            {renderMetricTrendCell(loc.trend_data, (w) => w.critical_issues ?? 0, {
                              lowerIsBetter: true,
                              formatValue: (v) => `${Math.round(v)}`,
                              formatDelta: (d) => `${Math.round(Math.abs(d))}`,
                            })}
                          </td>
                          <td>
                            {renderStatusBadge(status)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
            </div>
          )}
        </section>

        {/* Department Compliance Trends Table */}
        <section className="trends-section" id="section-departmentTrends">
          {renderSectionHeading('🏥 Department Trends', 'department')}
          {renderWindowNote(
            <>
              Org-wide top 5 departments needing attention in the selected Compare period,
              ranked by risk then decline. Use <strong>?</strong> for ranking rules.
            </>
          )}

          {departmentTrends && departmentTrends.departments.length > 0 && (
            <div className="comparison-table-scroll">
                <table className="comparison-table comparison-table--metric-trends">
                  <thead>
                    <tr>
                      <th>Department</th>
                      <th>Calibration</th>
                      <th>PPM</th>
                      <th>Regulatory</th>
                      <th className="trend-assessment-col">Trend Assessment</th>
                      <th>Risk Level</th>
                    </tr>
                  </thead>
                  <tbody>
                    {departmentTrends.departments.map((dept) => {
                      const assessment = getDepartmentTrendAssessment(dept.trend_data);
                      const risk = getDepartmentRiskLevel(dept.trend_data);
                      return (
                        <tr key={`${dept.location_id}-${dept.department_id}`}>
                          <td className="metric-table-name metric-table-name--stacked">
                            <div className="metric-table-primary">
                              {dept.department_icon ? (
                                <span className="metric-table-icon" aria-hidden="true">{dept.department_icon}</span>
                              ) : null}
                              <span>{dept.department_name}</span>
                            </div>
                            <div className="metric-table-secondary">
                              <span className="metric-table-icon" aria-hidden="true">📍</span>
                              <span>{dept.location_name}</span>
                            </div>
                          </td>
                          <td>{renderMetricTrendCell(dept.trend_data, (w) => w.calibration ?? 0)}</td>
                          <td>{renderMetricTrendCell(dept.trend_data, (w) => w.ppm ?? 0)}</td>
                          <td>{renderMetricTrendCell(dept.trend_data, (w) => w.regulatory ?? 0)}</td>
                          <td className="trend-assessment-col">
                            <div className="trend-assessment-cell">
                              <span className={`forecast-badge forecast-badge--${assessment.status}`}>
                                {assessment.text}
                              </span>
                              {assessment.detailParts ? (
                                <div className="trend-assessment-detail">
                                  {assessment.detailParts.map((part, index) => (
                                    <span key={part.status}>
                                      {index > 0 ? ' · ' : null}
                                      <span className={`trend-assessment-part trend-assessment-part--${part.status}`}>
                                        {part.text}
                                      </span>
                                    </span>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          </td>
                          <td>{renderStatusBadge(risk)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
            </div>
          )}
        </section>

        {/* Financial Performance Trends */}
        <section className="trends-section" id="section-financial">
          {renderSectionHeading('💰 Financial Trends', 'financial')}
          {renderFinancialTrends()}
        </section>

        <TrendsAnalyticsHelpModal
          section={helpSection}
          onClose={() => setHelpSection(null)}
        />
      </div>
    );
  }

  // PART 2: LOCATION DEEP-DIVE ANALYSIS
  if (viewMode === 'part2') {
    const deepDiveLocation = getSelectedDeepDiveLocation();
    return (
      <div className="hybrid-trends-container">
        {/* Header Section */}
        <div className="trends-header">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <h1 className="page-title">📈 {selectedLocation?.location_name || 'Location'} Trends</h1>
            <button
              onClick={refetchAllData}
              className="btn-refresh"
              title="Refresh trends data"
              aria-label="Refresh trends data"
              style={{ padding: '0.5rem', fontSize: '1.2rem', border: 'none', background: 'none', cursor: 'pointer', lineHeight: 1, flexShrink: 0, marginTop: '0.25rem' }}
            >
              🔄
            </button>
          </div>
          <p className="page-subtitle" style={{ margin: 0 }}>
            8-week trajectory and forecast for this location.
          </p>
        </div>

        {error && <div className="trends-error">{error}</div>}

        {/* View Mode Toggle - Positioned at Top */}
        <div className="view-mode-toggle">
          <button className="view-mode-btn" onClick={() => setViewMode('part1')}>
            ← Back to scorecard
          </button>
        </div>

        {/* Quick Navigation Bar for Part 2 */}
        <div
          className={`quick-nav-bar admin-quick-nav${isCompactQuickNav ? ' admin-quick-nav--compact' : ''}`}
          style={{
            top: 'var(--app-header-offset)',
            zIndex: 55,
            padding: isCompactQuickNav ? '0.65rem 0 0.85rem' : undefined
          }}
        >
          <div className="quick-nav-label">Jump to</div>
          <div className="admin-quick-nav-scroll">
            <div
              className="quick-nav-links admin-quick-nav-links"
              style={{
                display: 'flex',
                flexWrap: isCompactQuickNav ? 'nowrap' : 'wrap',
                gap: '0.5rem',
                alignItems: 'center'
              }}
            >
              {[
                { label: '📊 Site metrics', id: 'section-siteMetrics' },
                { label: '🏥 Departments', id: 'section-departmentTrendsDeep' },
                { label: '📍 Across locations', id: 'section-acrossLocations' }
              ].map((link) => (
                <a
                  key={link.id}
                  href={`#${link.id}`}
                  className="quick-nav-link admin-quick-nav-link"
                  onClick={(e) => {
                    e.preventDefault();
                    const element = document.getElementById(link.id);
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Location Selector Buttons */}
        {deepDiveLocationTrends && deepDiveLocationTrends.locations.length > 0 && (
          <div className="location-buttons">
            <span className="location-buttons-label">Location</span>
            {deepDiveLocationTrends.locations.map(loc => (
              <button
                key={loc.location_id}
                className={`location-btn ${selectedLocation?.location_id === loc.location_id ? 'active' : ''}`}
                onClick={() => setSelectedLocation(loc)}
              >
                {loc.location_name}
              </button>
            ))}
          </div>
        )}

        {/* Compliance Metrics Trends (Location Deep-Dive) */}
        {deepDiveLocation && (
          <>
            <section className="trends-section" id="section-siteMetrics">
              <div className="trends-section-heading">
                <h2>📊 Site metrics — {deepDiveLocation.location_name}</h2>
                <button
                  type="button"
                  className="user-mgmt-help-btn"
                  onClick={() => setHelpSection('siteMetrics')}
                  aria-label="Site metrics help"
                  title="What do these metrics mean?"
                >
                  ?
                </button>
              </div>
              {renderWindowNote(
                <>
                  Rolling <strong>8-week</strong> window for this location (independent of the Scorecard
                  Compare control). Change columns compare the latest week to 8 weeks ago.
                  Health score is the average of Cal / PPM / Reg categories that have devices.
                </>
              )}
              {deepDiveLocation.trend_data && renderDeepDiveLocationStatusNote(deepDiveLocation)}

              {deepDiveLocation.trend_data && (
                <>
                  {/* Metrics Table */}
                  <table className="metrics-table">
                    <thead>
                      <tr>
                        <th>Metric</th>
                        <th>8-Week Trend</th>
                        <th>Current Value</th>
                        <th>Change (8 weeks)</th>
                        <th>Forecast</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Calibration Compliance */}
                      <tr>
                        <td className="metric-name-cell">Calibration Compliance</td>
                        <td className="chart-cell">
                          {renderDeepDiveSparkline(deepDiveLocation.trend_data, (w) => w.calibration ?? 0)}
                        </td>
                        <td className="metric-value-cell">{formatPercentage(deepDiveLocation.trend_data.week_0?.calibration || 0)}</td>
                        <td className="metric-change-cell">
                          {renderEightWeekChangeContent(deepDiveLocation.trend_data, (w) => w.calibration ?? 0)}
                        </td>
                        {renderForecastCell(deepDiveLocation.trend_data, (w) => w.calibration ?? 0, {
                          metricKey: 'calibration',
                        })}
                      </tr>

                      {/* PPM Compliance */}
                      <tr>
                        <td className="metric-name-cell">PPM Compliance</td>
                        <td className="chart-cell">
                          {renderDeepDiveSparkline(deepDiveLocation.trend_data, (w) => w.ppm ?? 0)}
                        </td>
                        <td className="metric-value-cell">{formatPercentage(deepDiveLocation.trend_data.week_0?.ppm || 0)}</td>
                        <td className="metric-change-cell">
                          {renderEightWeekChangeContent(deepDiveLocation.trend_data, (w) => w.ppm ?? 0)}
                        </td>
                        {renderForecastCell(deepDiveLocation.trend_data, (w) => w.ppm ?? 0, {
                          metricKey: 'ppm',
                        })}
                      </tr>

                      {/* Regulatory Compliance */}
                      <tr>
                        <td className="metric-name-cell">Regulatory Compliance</td>
                        <td className="chart-cell">
                          {renderDeepDiveSparkline(deepDiveLocation.trend_data, (w) => w.regulatory ?? 0)}
                        </td>
                        <td className="metric-value-cell">{formatPercentage(deepDiveLocation.trend_data.week_0?.regulatory || 0)}</td>
                        <td className="metric-change-cell">
                          {renderEightWeekChangeContent(deepDiveLocation.trend_data, (w) => w.regulatory ?? 0)}
                        </td>
                        {renderForecastCell(deepDiveLocation.trend_data, (w) => w.regulatory ?? 0, {
                          metricKey: 'regulatory',
                        })}
                      </tr>

                      {/* Overall Health Score */}
                      <tr>
                        <td className="metric-name-cell">Overall Health Score</td>
                        <td className="chart-cell">
                          {renderDeepDiveSparkline(deepDiveLocation.trend_data, (w) => w.health_score ?? 0)}
                        </td>
                        <td className="metric-value-cell">{formatPercentage(deepDiveLocation.trend_data.week_0?.health_score || 0)}</td>
                        <td className="metric-change-cell">
                          {renderEightWeekChangeContent(deepDiveLocation.trend_data, (w) => w.health_score ?? 0)}
                        </td>
                        {renderForecastCell(deepDiveLocation.trend_data, (w) => w.health_score ?? 0, {
                          metricKey: 'health_score',
                        })}
                      </tr>

                      {/* Critical Issues Count */}
                      <tr>
                        <td className="metric-name-cell">Critical Issues (open now)</td>
                        <td className="chart-cell">
                          {renderDeepDiveSparkline(deepDiveLocation.trend_data, (w) => w.critical_issues ?? 0, {
                            formatBarLabel: (v) => String(Math.round(v ?? 0)),
                          })}
                        </td>
                        <td className="metric-value-cell">{Math.round(deepDiveLocation.trend_data.week_0?.critical_issues || 0)}</td>
                        <td className="metric-change-cell">
                          {renderEightWeekChangeContent(deepDiveLocation.trend_data, (w) => w.critical_issues ?? 0, {
                            lowerIsBetter: true,
                            issues: true,
                          })}
                        </td>
                        {renderForecastCell(deepDiveLocation.trend_data, (w) => w.critical_issues ?? 0, {
                          lowerIsBetter: true,
                          issues: true,
                        })}
                      </tr>
                    </tbody>
                  </table>
                </>
              )}
            </section>

            {/* Department Trends within Location — uses shared compareMetric */}
            {deepDiveDepartmentTrends && deepDiveDepartmentTrends.departments.length > 0 && (
              <section className="trends-section" id="section-departmentTrendsDeep">
                <div className="trends-section-heading metric-explorer-heading">
                  <h2>🏥 Departments — {deepDiveLocation.location_name}</h2>
                  <div className="metric-explorer-heading-actions">
                    {renderCompareMetricSelect('departments')}
                    <button
                      type="button"
                      className="user-mgmt-help-btn"
                      onClick={() => setHelpSection('deepDiveDepartments')}
                      aria-label="Department trends help"
                      title="What do these metrics mean?"
                    >
                      ?
                    </button>
                  </div>
                </div>
                {renderWindowNote(
                  <>
                    Same rolling <strong>8 weeks</strong>. Metric is shared with Across locations.
                    Rows default to <strong>worst first</strong>; click column headers to re-sort.
                    {getCompareMetricConfig().key === 'critical_issues' && (
                      <>
                        {' '}
                        Each bar is <strong>open criticals that week</strong> (snapshot), not new
                        issues opened that week.
                      </>
                    )}
                  </>
                )}
                {(() => {
                  const metric = getCompareMetricConfig();
                  const summary = buildDeepDiveDepartmentInsightSummary(
                    deepDiveDepartmentTrends.departments,
                    metric.key
                  );
                  if (!summary) return null;
                  const { best, weakest, ranked, atRisk } = summary;
                  const formatVal = (v) => formatCompareMetricValue(v, metric.format);
                  const formatDelta = (d) => formatCompareMetricDelta(d, metric.format);
                  const steady = ranked.filter((r) => Math.abs(r.change) <= 0.3);
                  const hasWatch = atRisk.length > 0;
                  return renderWindowNote(
                    <>
                      <strong>{hasWatch ? `${metric.label} watch` : `8-week ${metric.label} summary`}.</strong>{' '}
                      Strongest gain: {best.dept.department_name} ({formatDelta(best.change)}, now{' '}
                      {formatVal(best.current)}).
                      {steady.length > 0 && (
                        <>
                          {' '}
                          Steady: {steady.map((r) => r.dept.department_name).join(', ')} (within ±0.3).
                        </>
                      )}
                      {weakest.dept.department_id !== best.dept.department_id && (
                        <>
                          {' '}
                          Slowest change: {weakest.dept.department_name} ({formatDelta(weakest.change)}).
                        </>
                      )}
                      {hasWatch && (
                        <>
                          {' '}
                          {metric.format === 'count' ? 'High critical volume' : 'Below 75%'}:{' '}
                          {atRisk
                            .map((r) => `${r.dept.department_name} (${formatVal(r.current)})`)
                            .join(', ')}
                          .
                        </>
                      )}
                    </>,
                    { insight: hasWatch }
                  );
                })()}

                {(() => {
                  const metric = getCompareMetricConfig();
                  const getValue = (w) => getBucketMetricValue(w, metric.key) ?? 0;
                  const rows = deepDiveDepartmentTrends.departments.map((dept) => {
                    const current = getBucketMetricValue(dept.trend_data?.week_0, metric.key);
                    const prior = getBucketMetricValue(dept.trend_data?.week_7, metric.key);
                    const rawGain =
                      current != null && prior != null ? current - prior : null;
                    const signedGain =
                      rawGain == null
                        ? null
                        : metric.lowerIsBetter
                          ? -rawGain
                          : rawGain;
                    return { dept, current, prior, rawGain, signedGain };
                  });
                  const sortedRows = sortCompareRows(rows, deepDiveDeptSort, metric, {
                    nameKey: (r) => r.dept.department_name || '',
                  });
                  return (
                <table className="metrics-table">
                  <thead>
                    <tr>
                      {renderSortableTh('dept', deepDiveDeptSort, 'name', 'Department')}
                      <th>8-Week Trend</th>
                      {renderSortableTh(
                        'dept',
                        deepDiveDeptSort,
                        'current',
                        getCompareMetricCurrentHeader(metric)
                      )}
                      {renderSortableTh('dept', deepDiveDeptSort, 'delta', 'Change (8 weeks)')}
                      <th>Status & Forecast</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map(({ dept }) => (
                      <tr key={dept.department_id}>
                        <td className="metric-name-cell">
                          {dept.department_icon ? (
                            <span className="metric-table-icon" aria-hidden="true">{dept.department_icon}</span>
                          ) : null}
                          <span>{dept.department_name}</span>
                        </td>
                        <td className="chart-cell">
                          {renderDeepDiveSparkline(dept.trend_data, getValue, {
                            formatBarLabel: metric.format === 'count'
                              ? (v) => String(Math.round(v ?? 0))
                              : undefined,
                          })}
                        </td>
                        <td className="metric-value-cell">
                          {formatCompareMetricValue(
                            getBucketMetricValue(dept.trend_data?.week_0, metric.key),
                            metric.format
                          )}
                        </td>
                        <td className="metric-change-cell">
                          {renderEightWeekChangeContent(dept.trend_data, getValue, {
                            lowerIsBetter: metric.lowerIsBetter,
                            issues: metric.key === 'critical_issues',
                          })}
                        </td>
                        {renderForecastCell(dept.trend_data, getValue, {
                          lowerIsBetter: metric.lowerIsBetter,
                          issues: metric.key === 'critical_issues',
                          metricKey: metric.key === 'health_score' ? 'health_score' : metric.key,
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                  );
                })()}
              </section>
            )}

            {/* Across locations — same shared compareMetric as Departments above */}
            {deepDiveLocationTrends && deepDiveLocationTrends.locations.length > 0 && (
              <section className="trends-section" id="section-acrossLocations">
                <div className="trends-section-heading metric-explorer-heading">
                  <h2>📍 Across locations</h2>
                  <div className="metric-explorer-heading-actions">
                    {renderCompareMetricSelect('locations')}
                    <button
                      type="button"
                      className="user-mgmt-help-btn"
                      onClick={() => setHelpSection('acrossLocations')}
                      aria-label="Across locations help"
                      title="What do these metrics mean?"
                    >
                      ?
                    </button>
                  </div>
                </div>
                {renderWindowNote(
                  <>
                    Peer comparison for the selected Metric on the same rolling{' '}
                    <strong>8 weeks</strong>. Changing Metric also updates Departments above.
                    Rows default to <strong>worst first</strong>; click column headers to re-sort.
                    {getCompareMetricConfig().key === 'critical_issues' && (
                      <>
                        {' '}
                        Counts are <strong>currently open</strong> critical complaints, not weekly
                        new tickets.
                      </>
                    )}
                  </>
                )}

                {(() => {
                  const metric = getCompareMetricConfig();
                  const locationRows = deepDiveLocationTrends.locations.map((loc) => {
                    const current = getBucketMetricValue(loc.trend_data?.week_0, metric.key);
                    const prior = getBucketMetricValue(loc.trend_data?.week_7, metric.key);
                    const rawGain =
                      current != null && prior != null ? current - prior : null;
                    const signedGain =
                      rawGain == null
                        ? null
                        : metric.lowerIsBetter
                          ? -rawGain
                          : rawGain;
                    return { loc, current, prior, rawGain, signedGain };
                  });
                  const sortedLocationRows = sortCompareRows(
                    locationRows,
                    deepDiveLocSort,
                    metric,
                    { nameKey: (r) => r.loc.location_name || '' }
                  );
                  const signedGains = sortedLocationRows
                    .map((r) => r.signedGain)
                    .filter((g) => g != null);
                  const maxGain = signedGains.length ? Math.max(...signedGains) : null;
                  const minGain = signedGains.length ? Math.min(...signedGains) : null;

                  return (
                    <div className="comparison-table-scroll">
                      <table className="comparison-table comparison-table--snapshot">
                        <thead>
                          <tr>
                            {renderSortableTh('loc', deepDiveLocSort, 'name', 'Location')}
                            {renderSortableTh(
                              'loc',
                              deepDiveLocSort,
                              'current',
                              getCompareMetricCurrentHeader(metric),
                              { className: 'num-col' }
                            )}
                            {renderSortableTh(
                              'loc',
                              deepDiveLocSort,
                              'prior',
                              '8 Weeks Ago',
                              { className: 'num-col' }
                            )}
                            {renderSortableTh('loc', deepDiveLocSort, 'delta', 'Δ', {
                              className: 'num-col',
                            })}
                            <th className="num-col">Trajectory</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedLocationRows.map(({ loc, current, prior, rawGain, signedGain }) => {
                            const isSelected =
                              loc.location_id === deepDiveLocation.location_id;
                            const trajectory = getTrajectoryForGain(signedGain ?? 0, {
                              maxGain,
                              minGain,
                            });
                            const displayGainStatus =
                              signedGain == null
                                ? 'stable'
                                : signedGain > 0.3
                                  ? 'improving'
                                  : signedGain < -0.3
                                    ? 'declining'
                                    : 'stable';
                            return (
                              <tr
                                key={loc.location_id}
                                className={isSelected ? 'metric-explorer-row--selected' : undefined}
                              >
                                <td className="metric-table-name">
                                  📍 {loc.location_name}
                                  {isSelected ? ' (this site)' : ''}
                                </td>
                                <td className="num-cell">
                                  {formatCompareMetricValue(current, metric.format)}
                                </td>
                                <td className="num-cell">
                                  {formatCompareMetricValue(prior, metric.format)}
                                </td>
                                <td className="num-cell">
                                  <span
                                    className={`metric-change-text metric-change-text--${displayGainStatus}`}
                                  >
                                    {formatCompareMetricDelta(rawGain, metric.format)}
                                  </span>
                                </td>
                                <td className="num-col">
                                  <span
                                    className={`forecast-badge forecast-badge--${trajectory.status}`}
                                  >
                                    {trajectory.label}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </section>
            )}
          </>
        )}

        <TrendsAnalyticsHelpModal
          section={helpSection}
          onClose={() => setHelpSection(null)}
        />
      </div>
    );
  }

  return null;
};

export default HybridTrendsAnalytics;
